<template>
  <div class="verification-details">
    <template v-if="allDataLoaded">
      <template v-if="isFailed">
        <error-message :message="errorMessage | globalize" />
      </template>
      <template v-else>
        <span class="pull-right">
          <export-pdf-button
            ref="exportPdfBtn"
            :data-loaded="allDataLoaded"
            document-id="verification-as-pdf"
            :route-path="$route.path"
          />
        </span>
        <div
          class="verification-details__request app__block"
          id="verification-as-pdf">
          <h2 class="verification-details__title">
            {{ 'verification-details.title-details' | globalize }}
          </h2>
          <ul class="app-key-value__list">
            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-id' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.id">
                <copyable :copy-text="verification.id.toString()">
                  {{ verification.id || '-' }}
                </copyable>
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-user-id' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.userId">
                <copyable :copy-text="verification.userId.toString()">
                  {{ verification.userId || '-' }}
                </copyable>
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-verify-status' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.status">
                {{ verification.status | formatVerificationStatus }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-type' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.type">
                {{ verification.type | formatVerificationType }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-subtype' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.subtype">
                {{ verification.subtype | formatVerificationSubtype }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-created' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.createdAt">
                {{ verification.createdAt | formatDateDMYT }}
              </span>
            </li>
          </ul>

          <div class="verification-details__actions app__form-actions">
            <template
              v-if="
                verification.status === VERIFICATION_PENDING &&
                  verification.type !== VERIFICATION_TYPE_MANUAL &&
                  verification.type !== VERIFICATION_TYPE_KYB">
              <div class="verification-details__actions-wrp">
                <permission-wrapper
                  skip-alert
                  page-name="verifications"
                  resource-name="verification"
                  action-name="update_foreign">
                  <button
                    data-html2canvas-ignore
                    v-ripple
                    class="app__form-submit-btn app__button-raised"
                    @click="submit(true)"
                    :disabled="isDisabled"
                  >
                    {{ 'verification-details.btn-approve' | globalize }}
                  </button>

                  <button
                    data-html2canvas-ignore
                    v-ripple
                    class="app__form-submit-btn app__button-raised--danger"
                    @click="submit(false)"
                    :disabled="isDisabled"
                  >
                    {{ 'verification-details.btn-reject' | globalize }}
                  </button>
                </permission-wrapper>
              </div>
            </template>

            <div class="verification-details__actions-wrp">
              <permission-wrapper
                skip-alert
                page-name="user"
                resource-name="user"
                action-name="read_foreign">
                <button
                  data-html2canvas-ignore
                  v-ripple
                  class="app__form-submit-btn app__button-raised"
                  @click="redirectToUser(verification.userId)"
                >
                  {{ 'verification-details.btn-user' | globalize }}
                </button>
              </permission-wrapper>
            </div>
          </div>

          <template v-if="verification.type === VERIFICATION_TYPE_MANUAL">
            <template v-if="verification.isNotEmptySumsubKyc">
              <div class="verification-details__section break-after">
                <verification-details-sumsub-applicant-info
                  :verification="verification"
                  :applicant-info="applicantInfo"
                  :docs="applicantDocs"
                />
              </div>
            </template>

            <div class="verification-details__section">
              <template v-if="isLoadedPep">
                <verification-details-pep-info
                  :verification-pep-info="verificationPepInfo"
                />
              </template>
              <template v-if="isLoadedUserDetails">
                <verification-cdd-form
                  :verification-details="verification"
                  :user-details="userDetails"
                  :user-addresses="userAddresses"
                />
              </template>
              <template v-else>
                <loader
                  message-id="loading"
                  position-center />
              </template>
            </div>
          </template>

          <template v-if="verification.type === VERIFICATION_TYPE_KYB">
            <div class="verification-details__section">
              <verification-details-company-info
                :verification="verification"
                :beneficiaries="beneficiaries"
                :company-info="companyInfo"
                :company-docs="companyDocs"
              />
            </div>
            <template v-if="isLoadedUserDetails">
              <verification-bdd-form
                :verification-details="verification"
                :user-details="userDetails"
                :user-addresses="userAddresses"
              />
            </template>
          </template>

          <template
            v-if="verification.isNotEmptyData && verification.type !== VERIFICATION_TYPE_KYB">
            <div class="verification-details__section break-before">
              <h3 class="verification-details__subtitle">
                {{ 'verification-details.title-data' | globalize }}
              </h3>

              <details-reader
                :formatters="formatters"
                :details="verification.data" />
            </div>
          </template>

          <div class="break-after">
            <verification-details-photos
              :verification-details="verification"
            />
          </div>
          <template v-if="verification.isNotEmptyReason">
            <div class="verification-details__section break-before">
              <h3 class="verification-details__subtitle">
                {{ 'verification-details.title-reason' | globalize }}
              </h3>
              <details-reader :details="reason" />
            </div>
          </template>

          <template v-if="verification.isNotEmptyLogs">
            <div class="verification-details__section break-before">
              <h3 class="verification-details__subtitle">
                {{ 'verification-details.title-logs' | globalize }}
              </h3>
              <template v-for="(val, i) in verification.logs">
                <details-reader
                  class="verification-details__log"
                  :key="i"
                  :details="val" />
              </template>
            </div>
          </template>

          <template>
            <a-divider />
            <verification-comment-form
              class="break-before"
              :verification="verification"
            />
          </template>
        </div>
      </template>
    </template>
    <template v-else>
      <loader
        message-id=""
        position-center />
    </template>
  </div>
</template>

<script>
import Loader from '@/vue/common/Loader'
import ErrorMessage from '@/vue/common/ErrorMessage'
import DetailsReader from '@/vue/common/DetailsReader/DetailsReader'
import Copyable from '@/vue/common/copyable/Copyable'
import ADivider from 'ant-design-vue/lib/divider'
import VerificationCDDForm from '@/vue/forms/VerificationCDDForm/VerificationCDDForm'
import VerificationBDDForm from '@/vue/forms/VerificationBDDForm/VerificationBDDForm'
import VerificationDetailsPhotos from './components/VerificationDetailsPhotos'
import VerificationCommentsForm from '@/vue/forms/VerificationCommentForm'
import VerificationDetailsPepInfo from './components/VerificationDetailsPEPInfo'
import VerificationDetailsCompanyInfo from './components/VerificationDetailsCompanyInfo'
import VerificationDetailsSumsubApplicantInfo from './components/VerrificationDetailsSumsubApplicantInfo'
import { api } from '@/api'
import { VerificationRecord, VERIFICATION_SUBTYPES, VERIFICATION_SUBTYPES_AS_OBJECT } from '@/js/records/entities/verification.record'
import { UserRecord } from '@/js/records/entities/user.record'
import { VerificationPepInfoRecord } from '@/js/records/entities/verification-pep-info.record'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { errors } from '@/js/errors'
import { formatDate } from '@/js/utils/date.util'
import { vueRoutes } from '@/vue-router/routes'
import { imageUrlToSumsubDocs } from '@/js/helpers/urls'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import ExportPdfButton from '@/vue/common/export-pdf-button'

const VERIFICATION_PENDING = 'pending_manual'
const VERIFICATION_TYPE_MANUAL = 'manual'
const VERIFICATION_TYPE_KYB = 'kyb'

export default {
  name: 'verification-details',
  components: {
    Loader,
    ErrorMessage,
    VerificationDetailsPepInfo,
    VerificationDetailsPhotos,
    VerificationDetailsCompanyInfo,
    VerificationDetailsSumsubApplicantInfo,
    DetailsReader,
    'verification-bdd-form': VerificationBDDForm,
    'verification-comment-form': VerificationCommentsForm,
    'verification-cdd-form': VerificationCDDForm,
    'copyable': Copyable,
    'a-divider': ADivider,
    PermissionWrapper,
    ExportPdfButton,
  },
  mixins: [ PermissionMixin ],
  props: {
    id: { type: [String, Number], required: true },
  },
  data () {
    return {
      exporting: false,
      allDataLoaded: false,
      isCommentAdding: false,
      isLoaded: false,
      isLoadedUserDetails: false,
      isFailed: false,
      isDisabled: false,
      isLoadedPep: false,
      applicantInfo: {},
      applicantDocs: [],
      companyInfo: {},
      companyDocs: [],
      beneficiaries: [],
      documentsArray: [],
      verification: new VerificationRecord(),
      reason: [],
      sumsubAuthToken: '',
      userDetails: new UserRecord(),
      userAddresses: {},
      verificationSubtypes: VERIFICATION_SUBTYPES,
      verificationPepInfo: new VerificationPepInfoRecord(),
      errorMessage: 'verification-details.default-error-message',
      VERIFICATION_PENDING,
      VERIFICATION_TYPE_MANUAL,
      VERIFICATION_TYPE_KYB,
      formatters: [
        {
          key: 'birthDate',
          formatter: (value) => {
            const date = formatDate(value)
            return date
          },
        },
      ],
    }
  },

  async created () {
    this.allDataLoaded = false

    await this.getVerificationById()

    switch (this.verification.type) {
      case VERIFICATION_TYPE_MANUAL:
        this.isLoadedUserDetails = false
        await this.getUserDetails()
        if (!this.isForbidden('users', 'address', 'read_foreign')) {
          await this.getUserAddresses()
          await this.getUserChainalysisReports()
        }
        await this.getUserPEPInfo()
        if (this.verification.subtype === VERIFICATION_SUBTYPES_AS_OBJECT.provider_sumsub) {
          await this.getSumsubApplicant()
        }
        this.isLoadedUserDetails = true
        this.allDataLoaded = true

        break
      case VERIFICATION_TYPE_KYB:
        if (this.verification.kyb.info.companyInfo.companyName) {
          await this.getSumsubCompany()
          await this.getSumsubCompanyDocs()
          await this.getSumsubBeneficiaries()
        }

        await this.getUserDetails()
        if (!this.isForbidden('users', 'address', 'read_foreign')) {
          await this.getUserAddresses()
        }
        await this.getUserPEPInfo()
        this.allDataLoaded = true
        break
      default:
        this.allDataLoaded = true
        break
    }
    // export to pdf from verifications-list stuff
    //  $refs exists in component after nextTick
    this.$nextTick(async () => {
      if (this.allDataLoaded && !this.isFailed && /export$/.test(this.$route.path)) {
        this.exporting = true
        const tm = setTimeout(async () => {
          clearTimeout(tm)
          await this.$refs.exportPdfBtn.exportToPDF(this.allDataLoaded)
          this.exporting = false
          window.history.back()
        }, 2000)
      }
    })
  },

  methods: {
    async getVerificationById () {
      this.isLoaded = false

      try {
        let { data } = await api.getWithSignature(`/verifications/${this.id}`)
        this.verification = new VerificationRecord(data)

        this.reason = { details: this.verification.reason }
      } catch (error) {
        this.isFailed = true
        switch (error.constructor) {
          case errors.NotFoundError:
            this.errorMessage = 'verification-details.not-found-error-message'
            break
          default:
            this.errorMessage = 'verification-details.default-error-message'
        }
        ErrorHandler.processWithoutFeedback(error)
      }
      this.isLoaded = true
    },

    async getUserDetails () {
      try {
        let { data } = await api.getWithSignature(
          `/userdata/${this.verification.userId}`
        )
        this.userDetails = new UserRecord(data)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getUserPEPInfo () {
      this.isLoadedPep = false
      try {
        let { data } = await api.getWithSignature(
          `/users/${this.verification.userId}/pep_screening`
        )
        this.verificationPepInfo = new VerificationPepInfoRecord(data[0])
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoadedPep = true
      }
    },

    async getUserAddresses () {
      try {
        const query = {
          filter: clearObject({
            user_id: this.verification.userId,
          }),
        }
        let { data } = await api.getWithSignature('/addresses', query)

        const ethereumAddresses = data.map(item => ({
          address: item.address,
          riskLevel: item.riskLevel,
          cpkAddress: item.cpkAddress,
          reportURL: item.reportUrl,
          chainName: item.chainName,
          chainId: item.chainId,
          explorerUrl: item.explorerUrl,
        }))

        this.userAddresses = { ethereumAddresses }
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getUserChainalysisReports () {
      const promises = []
      const addresses = {}
      try {
        this.userAddresses.ethereumAddresses.forEach(record => {
          addresses[record.address] = 1
        })
        Object.keys(addresses).forEach(address => {
          const promise = api.getWithSignature(
            `/addresses/${address}/chainalysis-risk-report`
          )
          promises.push(promise)
        })
        const responses = await Promise.all(promises)
        this.userAddresses.ethereumAddresses.forEach(record => {
          const response = responses.find(r => r.data.id === record.address)
          record.chainalysis = response.data
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getSumsubCompany () {
      const { id } = this.verification.kyb

      this.isLoaded = false
      try {
        const companyInfo = await api.getWithSignature(
          `/sumsub/applicant/${id}/company`
        )
        delete companyInfo.data.beneficiaries

        this.companyInfo = companyInfo.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
      }
    },

    async getSumsubApplicant () {
      const { id } = this.verification.data.kyc

      this.isLoaded = false
      try {
        const applicantInfo = await api.getWithSignature(
          `/sumsub/applicant/${id}/individual`
        )
        const docs = await api.getWithSignature(
          `/sumsub/applicant/${id}/individual/docs`
        )

        this.applicantInfo = applicantInfo.data
        this.applicantDocs = docs.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
      }
    },

    async getSumsubBeneficiaries () {
      const { kyb } = this.verification
      const { beneficiaries } = kyb.info.companyInfo

      this.isLoaded = false
      try {
        const promises = []
        const docsPromises = []

        beneficiaries.forEach(row => {
          const promise = api.getWithSignature(
            `/sumsub/applicant/${row.applicantId}/individual`
          )
          promises.push(promise)

          const docPromise = api.getWithSignature(
            `/sumsub/applicant/${row.applicantId}/individual/docs`
          )
          docsPromises.push(docPromise)
        })
        const beneficiaryResponses = await Promise.all(promises)
        const docsResponses = await Promise.all(docsPromises)

        beneficiaries.forEach((row, index) => {
          beneficiaries[index] = {
            ...beneficiaryResponses[index].data,
            ...row,
            docs: docsResponses[index].data,
          }
        })
        beneficiaries.forEach(beneficiary => {
          beneficiary.docs.forEach(doc => {
            doc.url = imageUrlToSumsubDocs(beneficiary.inspectionId, doc.imageId[0])
          })
        })

        this.beneficiaries = beneficiaries
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
      }
    },

    async getSumsubCompanyDocs () {
      const { id } = this.verification.kyb

      this.isLoaded = false
      try {
        const companyDocs = await api.getWithSignature(
          `/sumsub/applicant/${id}/company/docs`
        )

        this.companyDocs = companyDocs.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
      }
    },

    async submit (isApprove) {
      this.isDisabled = true

      const body = JSON.stringify({
        data: {
          type: 'update_verification',
          attributes: {
            status: isApprove ? 'approved' : 'rejected',
          },
        },
      })

      try {
        await api.patchWithSignature(`/verifications/${this.verification.id}`, body)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.isDisabled = false
      this.getVerificationById()
    },

    redirectToUser (id) {
      this.$router.push({
        name: vueRoutes.userDetails.name,
        params: { id },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/vue/forms/app-form';
@import '~ant-design-vue/lib/divider/style/index.css';

.verification-details__title {
  margin-bottom: 4rem;
}

.verification-details__subtitle {
  margin-bottom: 2rem;
}

.verification-details__section {
  flex-direction: column;
  margin-top: 3rem;
}

.verification-details__actions {
  justify-content: space-between;
}

.verification-details__log {
  padding: 2rem 0;
  border-bottom: solid 0.1rem;

  &:last-child {
    border: none;
  }
}

.verification-details__actions-wrp {
  display: flex;
  gap: 1rem;
}

.verification-details__img-wrp {
  display: flex;
  gap: 1rem;
  max-width: 15rem;
  width: 100%;
}

.verification-details__dropdown-btn {
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
}

.verification-details__dropdown-btn-icon {
  display: block;
  font-size: 2rem;
  height: 2rem;
  width: 2rem;
  line-height: 1.8rem;
  transition: all 0.3s;
}
</style>
